import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { 
  clearCacheType, 
  invalidateByPattern, 
  clearAllCache,
  selectCacheStats,
  selectCacheHitRatio,
  selectCacheSizeByType,
  cleanupExpiredEntries
} from '../redux/cache/cacheSlice';
import { 
  CACHE_CONFIGS, 
  CACHE_INVALIDATION_PATTERNS,
  getUserCachePattern
} from '../utils/cacheUtils';

/**
 * Custom hook for cache management and debugging
 * @returns {Object} Cache management functions and stats
 */
export const useCache = () => {
  const dispatch = useDispatch();
  
  // Cache statistics
  const cacheStats = useSelector(selectCacheStats);
  const cacheHitRatio = useSelector(selectCacheHitRatio);
  
  // Cache sizes for different types
  const healthParamsCacheSize = useSelector(state => 
    selectCacheSizeByType(state, 'healthParams')
  );
  const appointmentsCacheSize = useSelector(state => 
    selectCacheSizeByType(state, 'appointments')
  );
  const lipidProfileCacheSize = useSelector(state => 
    selectCacheSizeByType(state, 'lipidProfile')
  );
  const medicalHistoryCacheSize = useSelector(state => 
    selectCacheSizeByType(state, 'medicalHistory')
  );

  // Cache clearing functions
  const clearHealthParamsCache = useCallback(() => {
    dispatch(clearCacheType({ cacheType: 'healthParams' }));
  }, [dispatch]);

  const clearAppointmentsCache = useCallback(() => {
    dispatch(clearCacheType({ cacheType: 'appointments' }));
  }, [dispatch]);

  const clearLipidProfileCache = useCallback(() => {
    dispatch(clearCacheType({ cacheType: 'lipidProfile' }));
  }, [dispatch]);

  const clearMedicalHistoryCache = useCallback(() => {
    dispatch(clearCacheType({ cacheType: 'medicalHistory' }));
  }, [dispatch]);

  const clearAllCaches = useCallback(() => {
    dispatch(clearAllCache());
  }, [dispatch]);

  // Pattern-based invalidation functions
  const invalidateHealthParamsByDate = useCallback((date) => {
    dispatch(invalidateByPattern({
      cacheType: 'healthParams',
      pattern: CACHE_INVALIDATION_PATTERNS.HEALTH_PARAMS_BY_DATE(date)
    }));
  }, [dispatch]);

  const invalidateHealthParamsByPatient = useCallback((patientId) => {
    dispatch(invalidateByPattern({
      cacheType: 'healthParams',
      pattern: CACHE_INVALIDATION_PATTERNS.HEALTH_PARAMS_BY_PATIENT(patientId)
    }));
  }, [dispatch]);

  const invalidateUserData = useCallback((userId) => {
    // Clear all cache for a specific user
    const pattern = getUserCachePattern(userId);
    
    Object.keys(CACHE_CONFIGS).forEach(cacheType => {
      dispatch(invalidateByPattern({
        cacheType: cacheType.toLowerCase().replace('_', ''),
        pattern
      }));
    });
  }, [dispatch]);

  // Cleanup expired entries for all cache types
  const cleanupExpiredCache = useCallback(() => {
    Object.entries(CACHE_CONFIGS).forEach(([cacheType, config]) => {
      dispatch(cleanupExpiredEntries({
        cacheType: cacheType.toLowerCase().replace('_', ''),
        expiryTime: config.expiry
      }));
    });
  }, [dispatch]);

  // Auto cleanup on component mount and periodically
  useEffect(() => {
    // Initial cleanup
    cleanupExpiredCache();

    // Setup periodic cleanup (every 5 minutes)
    const cleanupInterval = setInterval(() => {
      cleanupExpiredCache();
    }, 5 * 60 * 1000);

    return () => clearInterval(cleanupInterval);
  }, [cleanupExpiredCache]);

  // Memoized cache summary
  const cacheSummary = useMemo(() => ({
    stats: {
      hits: cacheStats.hits,
      misses: cacheStats.misses,
      invalidations: cacheStats.invalidations,
      hitRatio: `${cacheHitRatio}%`
    },
    sizes: {
      healthParams: healthParamsCacheSize,
      appointments: appointmentsCacheSize,
      lipidProfile: lipidProfileCacheSize,
      medicalHistory: medicalHistoryCacheSize,
      total: healthParamsCacheSize + appointmentsCacheSize + lipidProfileCacheSize + medicalHistoryCacheSize
    },
    config: CACHE_CONFIGS
  }), [
    cacheStats,
    cacheHitRatio,
    healthParamsCacheSize,
    appointmentsCacheSize,
    lipidProfileCacheSize,
    medicalHistoryCacheSize
  ]);

  return {
    // Cache statistics and info
    stats: cacheStats,
    hitRatio: cacheHitRatio,
    summary: cacheSummary,
    
    // Cache sizes
    sizes: {
      healthParams: healthParamsCacheSize,
      appointments: appointmentsCacheSize,
      lipidProfile: lipidProfileCacheSize,
      medicalHistory: medicalHistoryCacheSize
    },

    // Cache clearing functions
    clear: {
      healthParams: clearHealthParamsCache,
      appointments: clearAppointmentsCache,
      lipidProfile: clearLipidProfileCache,
      medicalHistory: clearMedicalHistoryCache,
      all: clearAllCaches
    },

    // Selective invalidation functions
    invalidate: {
      healthParamsByDate: invalidateHealthParamsByDate,
      healthParamsByPatient: invalidateHealthParamsByPatient,
      userData: invalidateUserData
    },

    // Maintenance functions
    cleanup: {
      expired: cleanupExpiredCache
    }
  };
};

/**
 * Hook specifically for health parameters cache management
 * @returns {Object} Health-specific cache functions
 */
export const useHealthParamsCache = () => {
  const { clear, invalidate, sizes, stats } = useCache();
  
  const clearCurrentUserHealthParams = useCallback(() => {
    const userType = localStorage.getItem("userType");
    const userId = localStorage.getItem("userId"); // Assuming user ID is stored
    
    if (userId) {
      invalidate.healthParamsByPatient(userId);
    } else {
      clear.healthParams();
    }
  }, [clear.healthParams, invalidate.healthParamsByPatient]);

  const invalidateHealthParamsToday = useCallback(() => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    invalidate.healthParamsByDate(today);
  }, [invalidate.healthParamsByDate]);

  return {
    size: sizes.healthParams,
    clear: clear.healthParams,
    clearCurrentUser: clearCurrentUserHealthParams,
    invalidateToday: invalidateHealthParamsToday,
    stats
  };
};

/**
 * Hook for cache debugging - only use in development
 * @returns {Object} Debug functions and information
 */
export const useCacheDebug = () => {
  const cache = useSelector(state => state.cache);
  const { summary } = useCache();

  const logCacheState = useCallback(() => {
    console.group('🗄️ Cache State');
    console.log('Summary:', summary);
    console.log('Full Cache State:', cache);
    console.groupEnd();
  }, [cache, summary]);

  const logCacheStats = useCallback(() => {
    console.group('📊 Cache Statistics');
    console.log(`Hit Ratio: ${summary.stats.hitRatio}`);
    console.log(`Total Hits: ${summary.stats.hits}`);
    console.log(`Total Misses: ${summary.stats.misses}`);
    console.log(`Total Invalidations: ${summary.stats.invalidations}`);
    console.log('Cache Sizes:', summary.sizes);
    console.groupEnd();
  }, [summary]);

  const logCacheEfficiency = useCallback(() => {
    const efficiency = parseFloat(summary.stats.hitRatio);
    let status = '🔴 Poor';
    
    if (efficiency >= 80) {
      status = '🟢 Excellent';
    } else if (efficiency >= 60) {
      status = '🟡 Good';
    } else if (efficiency >= 40) {
      status = '🟠 Fair';
    }

    console.log(`Cache Efficiency: ${efficiency}% ${status}`);
    
    if (efficiency < 60) {
      console.warn('💡 Consider reviewing cache expiration times or API call patterns');
    }
  }, [summary.stats.hitRatio]);

  // Only enable in development
  const isDevelopment = process.env.NODE_ENV === 'development';

  return isDevelopment ? {
    logState: logCacheState,
    logStats: logCacheStats,
    logEfficiency: logCacheEfficiency,
    summary
  } : {
    logState: () => {},
    logStats: () => {},
    logEfficiency: () => {},
    summary
  };
};

export default useCache;
